import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getOr } from 'lodash/fp';
import tickets from './api';

const name = 'tickets';

const fetchTicket = createAsyncThunk('tickets/fetchTicket', async (payload) => {
  const response = await tickets.fetchTicketEvent(payload);
  return response;
});

const getListEvents = createAsyncThunk(
  'tickets/publish-events/',
  async (payload) => {
    const response = await tickets.getListEvents(payload);
    return response;
  }
);
const checkIn = createAsyncThunk(
  'tickets/checkIn/',
  async (payload, { rejectWithValue }) => {
    try {
      return await tickets.checkIn(payload);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
const resendCheckInWaiver = createAsyncThunk(
  'tickets/checkIn/waiver/resend',
  async (payload) => {
    const response = await tickets.resendCheckInWaiver(payload);
    return response;
  }
);
const getEventDetail = createAsyncThunk('tickets/events/', async (payload) => {
  const response = await tickets.getEventDetail(payload);
  return response;
});
const resetTicket = createAsyncThunk('memberships/resetTicket', async () => {
  return true;
});
const reservedOrder = createAsyncThunk(
  'tickets/reserved-order',
  async (orderId) => {
    const response = await tickets.reservedOrder(orderId);
    return response;
  }
);
const validateTicketCoupon = createAsyncThunk(
  'tickets/validate-ticket-coupon',
  async (payload) => {
    const response = await tickets.validateTicketCoupon(payload);
    return response;
  }
);

const calculateTicketCost = createAsyncThunk(
  'tickets/calculate',
  async (payload) => {
    const response = await tickets.calculateTicketCost(payload);
    return response;
  }
);

const submitFreeEvent = createAsyncThunk(
  'tickets/free-ticket-checkout',
  async (payload) => {
    const response = await tickets.submitTicketFreeAPI(payload);
    return response;
  }
);

const getTicketSetup = createAsyncThunk('tickets/misc', async (payload) => {
  const response = await tickets.getTicketSetupApi(payload);
  return response;
});

const getTickets = createAsyncThunk('ticket/getTickets', async (params) => {
  const response = await tickets.getTickets(params);
  return response;
});

const { actions, reducer } = {
  ...createSlice({
    name,
    initialState: {
      isLoading: false,
      error: false,
      ticketConfig: {},
      formData: {},
      ticketEvents: [],
      eventDetail: {},
      checkInDetail: {},
      ticketSetup: {},
      verificationCode: '',
      membershipEventsCount: 0,
      tickets: {
        data: [],
        total: 0,
      },
    },
    reducers: {
      setFormData: (state, { payload }) => {
        state.formData = { ...state.formData, ...payload };
      },
      setVerificationCode: (state, { payload }) => {
        state.verificationCode = payload;
      },
      resetFormData: (state) => {
        state.formData = {};
      },
    },
    extraReducers: (builder) => {
      builder
        // Fetch organization
        .addCase(fetchTicket.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(fetchTicket.fulfilled, (state, action) => {
          state.isLoading = false;
          state.ticketConfig = action.payload;
        })
        .addCase(fetchTicket.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })

        .addCase(getTickets.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(getTickets.fulfilled, (state, action) => {
          state.isLoading = false;
          state.tickets = getOr(
            {
              data: [],
              total: 0,
            },
            'payload',
            action
          );
        })
        .addCase(getTickets.rejected, (state, action) => {
          state.isLoading = false;
          state.error = action.payload;
        })
        .addCase(getListEvents.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(getListEvents.fulfilled, (state, action) => {
          state.isLoading = false;
          state.ticketEvents = action.payload?.data || [];
          state.membershipEventsCount = action.payload?.membershipEventsCount;
        })

        .addCase(getEventDetail.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(getEventDetail.fulfilled, (state, action) => {
          state.isLoading = false;
          state.eventDetail = action.payload;
        })

        .addCase(checkIn.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(checkIn.fulfilled, (state, action) => {
          state.isLoading = false;
          state.checkInDetail = action.payload;
        })
        .addCase(checkIn.rejected, (state) => {
          state.isLoading = false;
          state.checkInDetail = { error: true };
        })

        .addCase(resetTicket.fulfilled, (state) => {
          state.isLoading = false;
          state.checkInDetail = {};
        })

        .addCase(getTicketSetup.pending, (state) => {
          state.isLoading = true;
        })
        .addCase(getTicketSetup.fulfilled, (state, action) => {
          state.isLoading = false;
          state.ticketSetup = action.payload;
        });
    },
  }),
};

const selectors = {
  selectIsLoading: (state) => getOr('', 'isLoading', state[name]),
  selectTicketEvent: (state) => getOr('', 'ticketConfig', state[name]),
  selectFormData: (state) => getOr('', 'formData', state[name]),
  selectTicketEvents: (state) => getOr('', 'ticketEvents', state[name]),
  selectMembershipEventsCount: (state) =>
    getOr('', 'membershipEventsCount', state[name]),
  selectTicketConfig: (state) => getOr('', 'ticketConfig', state[name]),
  selectEventDetail: (state) => getOr('', 'eventDetail', state[name]),
  selectCheckInDetail: (state) => getOr('', 'checkInDetail', state[name]),
  selectTicketSetup: (state) => getOr('', 'ticketSetup', state[name]),
  selectVerificationCode: (state) => getOr({}, 'verificationCode', state[name]),
  selectTickets: (state) => getOr({}, 'tickets', state[name]),
};

export default {
  actions: {
    ...actions,
    fetchTicket,
    getListEvents,
    getEventDetail,
    checkIn,
    resendCheckInWaiver,
    resetTicket,
    reservedOrder,
    validateTicketCoupon,
    calculateTicketCost,
    submitFreeEvent,
    getTicketSetup,
    getTickets,
  },
  selectors,
  reducer,
  name,
};
